/* @flow */
import * as React from 'react';
import { showToast } from 'reducers/toasts';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { graphql, compose } from 'react-apollo';
import Button from 'components/ButtonDeprecated';
import Buttons from 'components/Buttons';
import CheckboxList from 'components/CheckboxList';
import ConfirmDialog from 'components/ConfirmDialog';
import ModalTrigger from 'components/ModalTrigger';
import Textarea from 'components/Textarea';
import Field from 'components/Field';
import Label from 'components/Label';
import gql from 'graphql-tag';

type PhoneCall = {
  uuid: string,
  note: string,
  reasons: string[],
};

type Props = {
  // the phone call uuid
  uuid: string,
  // apollo - the phone call from apollo
  phoneCall?: PhoneCall,
  // apollo - the mutation for apollo
  updatePhoneCall: (note?: string, reasons?: string[]) => Promise<void>,
  // redux - display a toast
  showToast: (text: string, kind: 'success' | 'error') => any,
  // callback called when the phone call was updated
  onSave: () => void,
};

const OPTIONS: Map<string, string> = new Map([
  ['academic_performance', 'Academic performance'],
  ['bad_behavior', 'Behavior'],
  ['general_progress', 'Update or check-in'],
  ['schedule_meeting', 'Schedule a meeting'],
  ['truancy', 'Attendance'],
  ['other', 'Other'],
  ['encouragement', 'Positive reinforcement'],
]);

type PhoneCallState = {
  reasons?: string[],
  note?: string,
};

// prettier-ignore
type State = {
  /* :: ...PhoneCallState,   */
  /* :: submitting: boolean, */
};

function stateFromPhoneCall(phoneCall?: PhoneCall): PhoneCallState {
  if (phoneCall) {
    return {
      note: phoneCall.note,
      reasons: phoneCall.reasons,
    };
  } else {
    return {
      reasons: [],
      note: '',
    };
  }
}

/* Form to edit a phone call note & reasons
 * The form loads the phone call is not present
 * in the apollo cache, inputs will be disabled will doing so.
 *
 */
class PhoneCallNoteForm extends React.Component<Props, State> {
  state: State = {
    ...stateFromPhoneCall(this.props.phoneCall),
    submitting: false,
  };

  componentWillReceiveProps(nextProps: Props) {
    if (!this.props.phoneCall && nextProps.phoneCall) {
      this.setState(stateFromPhoneCall(nextProps.phoneCall));
    }
  }

  handleReasonChange = (reasons: string[]) => {
    this.setState({ reasons });
  };

  handleNoteChange = (event: *) => {
    this.setState({ note: event.target.value });
  };

  handleSubmit = () => {
    const reasons = this.state.reasons;
    this.setState({ submitting: true });

    this.props
      .updatePhoneCall(this.state.note, reasons)
      .then(() => {
        this.props.onSave();
        this.props.showToast('Phone call note saved.', 'success');
        this.setState({ submitting: false });
      })
      .catch(e => {
        this.setState({ submitting: false });
        this.props.showToast('An error occurred while saving this note', 'error');
        console.debug('an error occured during PhoneCallNoteForm mutation', e);
      });
  };

  handleDelete = () => {
    this.props
      .updatePhoneCall('', [])
      .then(() => {
        this.props.onSave();
        this.props.showToast('Phone call note deleted.', 'success');
      })
      .catch(e => {
        this.props.showToast('An error occurred while deleting this note', 'error');
        console.error('an error occured during PhoneCallNoteForm mutation');
        console.debug(e, e && e.stack);
      });
  };

  render() {
    const disabled = !this.props.phoneCall || this.state.submitting;

    return (
      <div>
        <Field>
          <Label>Reason for call</Label>
          <CheckboxList
            options={OPTIONS}
            disabled={disabled ? Object.keys(OPTIONS) : undefined}
            onChange={this.handleReasonChange}
            checked={this.state.reasons}
            direction="columns"
          />
        </Field>
        <Field>
          <Label>Notes</Label>
          <Textarea
            rows={4}
            disabled={disabled}
            value={this.state.note}
            onChange={this.handleNoteChange}
            placeholder="Take private notes about the call"
          />
        </Field>
        <Buttons>
          {this.props.phoneCall &&
          (this.props.phoneCall.note || this.props.phoneCall.reasons.length) ? (
            <ModalTrigger
              modal={
                <ConfirmDialog
                  title="Are you sure you want to delete this note?"
                  actionButtonText="Delete"
                  actionButtonColor="apple"
                  cancelButtonText="Cancel"
                  onConfirm={this.handleDelete}
                />
              }>
              <Button data-test="delete-button" color="apple" level="secondary" disabled={disabled}>
                Delete
              </Button>
            </ModalTrigger>
          ) : null}
          <Button data-test="save-button" disabled={disabled} onClick={this.handleSubmit}>
            Save
          </Button>
        </Buttons>
      </div>
    );
  }
}

const withPhoneCall = graphql(
  gql`
    query PhoneCallInfo($uuid: String!) {
      phoneCall(uuid: $uuid) {
        uuid
        reasons
        note
      }
    }
  `,
  {
    options: ({ uuid }) => ({ variables: { uuid } }),
    props: ({ data }) => data,
  }
);

const UPDATE_PHONE_CALL = gql`
  mutation PhoneCallMutation($input: UpdatePhoneCallInput!) {
    updatePhoneCall(input: $input) {
      phoneCall {
        uuid
        note
        reasons
      }
      error {
        message
      }
    }
  }
`;

const withPhoneCallMutation = graphql(UPDATE_PHONE_CALL, {
  props: ({ ownProps, mutate }) => ({
    updatePhoneCall: (note, reasons) => {
      return mutate({
        variables: { input: { phoneCallUuid: ownProps.uuid, note, reasons } },
      });
    },
  }),
});

function mapDispatchToProps(dispatch: Function) {
  return bindActionCreators({ showToast }, dispatch);
}

export { PhoneCallNoteForm };
export { OPTIONS };

export default compose(
  withPhoneCall,
  withPhoneCallMutation,
  connect(undefined, mapDispatchToProps)
)(PhoneCallNoteForm);
